import {
  Component,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  OnInit,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  AddressRequest,
  PaymentIframeMessage,
  PaymentMethodData,
  PaymentMethodRequest,
} from '../core/types/payment-iframe-types';
import { PaymentMethodUpdateTokenProviderRequest } from '../core/types/payment-method-update.model';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { PaymentService } from '../core/services/payment.service';
import { ActivatedRoute } from '@angular/router';
import { AuthModel } from '../core/models/auth.model';
import { TranslationService } from '../modules/i18n';

export type FormType = 'card' | 'cvc';

@Component({
  selector: 'app-card-form',
  templateUrl: './card-form.component.html',
  styleUrl: './card-form.component.scss',
})
export class CardFormComponent implements OnInit, AfterViewInit, OnDestroy {
  cardControl: FormControl = new FormControl(null, [Validators.required]);
  formType: FormType = 'card';
  cardBrand: string = '';
  sourceUserId: number | null = null;
  sourceBusinessId: number | null = null;
  paymentMethodId: number | null = null;
  marginless: boolean = false;
  collectZip: boolean = false;
  merchantName: string = '';
  merchantWebsite: string = '';
  merchantPhone: string = '';
  cardholderEmail: string = '';
  cardholderFirstName: string = '';
  cardholderLastName: string = '';
  private resizeObs?: ResizeObserver;
  private parentOrigin = '*';
  private authModel?: AuthModel;
  isAuthenticating = true;
  authError?: string;

  get showCvc(): boolean {
    return this.sourceUserId !== null || this.sourceBusinessId !== null;
  }

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private paymentService: PaymentService,
    private route: ActivatedRoute,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    const externalToken = this.route.snapshot.queryParamMap.get('token');
    const formTypeParam = this.route.snapshot.queryParamMap.get('formType');
    const cardBrandParam = this.route.snapshot.queryParamMap.get('cardBrand');
    const langParam = this.route.snapshot.queryParamMap.get('lang');
    const sourceUserIdParam = this.route.snapshot.queryParamMap.get('sourceUserId');
    const sourceBusinessIdParam = this.route.snapshot.queryParamMap.get('sourceBusinessId');
    const paymentMethodIdParam = this.route.snapshot.queryParamMap.get('paymentMethodId');
    const marginlessParam = this.route.snapshot.queryParamMap.get('marginless');
    const zipParam = this.route.snapshot.queryParamMap.get('zip');
    this.merchantName = this.route.snapshot.queryParamMap.get('merchantName')?.trim() ?? '';
    this.merchantWebsite = this.route.snapshot.queryParamMap.get('merchantWebsite')?.trim() ?? '';
    this.merchantPhone = this.route.snapshot.queryParamMap.get('merchantPhone')?.trim() ?? '';
    this.cardholderEmail = this.route.snapshot.queryParamMap.get('email')?.trim() ?? '';
    this.cardholderFirstName = this.route.snapshot.queryParamMap.get('firstName')?.trim() ?? '';
    this.cardholderLastName = this.route.snapshot.queryParamMap.get('lastName')?.trim() ?? '';

    // Set language from query param
    if (langParam) {
      this.translationService.setLanguage(langParam);
    }

    // Set form type from query param, default to 'card'
    if (formTypeParam === 'cvc') {
      this.formType = 'cvc';
    }

    // Set card brand for CVC validation (e.g., 'american-express' requires 4-digit CVC)
    if (cardBrandParam) {
      this.cardBrand = cardBrandParam;
    }

    // Set sourceUserId and sourceBusinessId for CVC flow
    if (sourceUserIdParam) {
      this.sourceUserId = parseInt(sourceUserIdParam, 10);
    }
    if (sourceBusinessIdParam) {
      this.sourceBusinessId = parseInt(sourceBusinessIdParam, 10);
    }

    // Set paymentMethodId for card update flow
    if (paymentMethodIdParam) {
      this.paymentMethodId = parseInt(paymentMethodIdParam, 10);
    }

    // Set marginless for removing container padding
    if (marginlessParam !== null) {
      this.marginless = marginlessParam === 'true' || marginlessParam === '1' || marginlessParam === '';
    }
    this.collectZip = zipParam === '1' || zipParam === 'true';

    if (!externalToken) {
      this.handleAuthFailure('Missing token query parameter.');
      return;
    }

    this.paymentService
      .loginWithExternal(externalToken)
      .pipe(
        tap((response: any) => {
          const authModel = new AuthModel();
          authModel.setAuth(response as AuthModel);
          this.authModel = authModel;
        }),
        catchError((err) => {
          const message =
            err?.error?.message ??
            'Unable to authenticate with the provided token.';
          this.handleAuthFailure(message);
          return EMPTY;
        }),
        finalize(() => {
          this.isAuthenticating = false;
        })
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    // Notify ready
    this.post({ type: 'PAYMENT_IFRAME_READY' });

    // Validation changes -> parent
    this.cardControl.statusChanges.subscribe(() => {
      const valid = this.cardControl.valid && !!this.cardControl.value;
      const errorType = this.formType === 'cvc' ? 'CVC_INVALID' : 'CARD_INVALID';
      const errors = valid ? undefined : [errorType];
      this.post({ type: 'PAYMENT_VALIDATION', valid, errors });
    });

    // Auto height
    this.resizeObs = new ResizeObserver(() => {
      const height = this.elRef.nativeElement.scrollHeight;
      console.log('height ' + height);
      this.post({ type: 'PAYMENT_HEIGHT', height });
    });
    this.resizeObs.observe(this.elRef.nativeElement);

    // Listen for submit from parent (window for browser iframe, document for React Native WebView)
    window.addEventListener('message', this.onMessage);
    document.addEventListener('message', this.onMessage as unknown as EventListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
    document.removeEventListener('message', this.onMessage as unknown as EventListener);
    this.resizeObs?.disconnect();
  }

  private onMessage = async (ev: MessageEvent) => {
    // In production, validate origin: if (ev.origin !== 'https://your-host.com') return;
    let msg: PaymentIframeMessage;

    // React Native WebView sends messages as strings
    if (typeof ev.data === 'string') {
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
    } else {
      msg = ev.data as PaymentIframeMessage;
    }

    if (msg.type !== 'PAYMENT_SUBMIT') return;

    if (this.formType === 'card') {
      this.handleCardSubmit(msg.userId, msg.address, msg.default, msg.stripeCustomerId);
    } else if (this.formType === 'cvc') {
      this.handleCvcSubmit(
        msg.userId,
        msg.stripeCustomerId,
        msg.paymentMethod!
      );
    }
  };

  private handleCardSubmit(userId: string, address?: AddressRequest, isDefault?: boolean, stripeCustomerId?: string) {
    const valid = this.cardControl.valid && !!this.cardControl.value;
    if (!valid) {
      this.post({
        type: 'PAYMENT_RESULT',
        status: 'error',
        errorCode: 'VALIDATION_FAILED',
        errorMessage: 'Card data invalid',
      });
      return;
    }

    const postalCode = `${this.cardControl.value?.postalCode ?? ''}`.trim();
    if (this.collectZip && !postalCode) {
      this.post({
        type: 'PAYMENT_RESULT',
        status: 'error',
        errorCode: 'VALIDATION_FAILED',
        errorMessage: 'Card data invalid',
      });
      return;
    }

    // If paymentMethodId is provided in card mode, use update flow
    if (this.paymentMethodId) {
      const formData = this.cardControl.value;
      const updateRequest = this.getCardUpdateFormData(
        formData,
        stripeCustomerId,
        address
      );
      this.updatePaymentMethod(userId, updateRequest);
    } else {
      const data = this.getFormData(address, isDefault, stripeCustomerId);
      this.addPaymentMethod(userId, data);
    }
  }

  private handleCvcSubmit(
    userId: string,
    stripeCustomerId: string | undefined,
    paymentMethod: PaymentMethodData
  ) {
    const valid = this.cardControl.valid && !!this.cardControl.value;
    if (!valid) {
      this.post({
        type: 'PAYMENT_RESULT',
        status: 'error',
        errorCode: 'VALIDATION_FAILED',
        errorMessage: 'CVC data invalid',
      });
      return;
    }
    const formData = this.cardControl.value;
    const updateRequest = this.getCvcFormData(
      formData.cvc,
      stripeCustomerId,
      paymentMethod
    );
    this.updatePaymentMethod(userId, updateRequest);
  }

  private post(message: PaymentIframeMessage) {
    const messageStr = JSON.stringify(message);

    // React Native WebView uses window.ReactNativeWebView
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(messageStr);
    } else {
      // Browser iframe uses window.parent.postMessage
      window.parent?.postMessage(message, this.parentOrigin);
    }
  }

  private cardAgreement(): Record<string, unknown> {
    const metadata: Record<string, string> = {};
    if (this.merchantName) {
      metadata.businessName = this.merchantName;
    }
    if (this.merchantWebsite) {
      metadata.businessWebsite = this.merchantWebsite;
    }
    if (this.merchantPhone) {
      metadata.businessPhone = this.merchantPhone;
    }
    const lang = this.translationService.getSelectedLanguage();
    const langCode = typeof lang === 'string' ? lang.trim() : '';
    if (langCode) {
      metadata.lang = langCode;
    }
    const agreement: Record<string, unknown> = {
      status: 'ACCEPTED',
      metadata,
    };
    if (this.cardholderEmail) {
      agreement.email = this.cardholderEmail;
    }
    return agreement;
  }

  private billingAddressFrom(
    postalCode: string,
    address?: AddressRequest,
  ): AddressRequest {
    return {
      id: address?.id ?? null,
      city: address?.city ?? null,
      state: address?.state ?? null,
      postalCode,
      firstLine: address?.firstLine ?? null,
      secondLine: address?.secondLine ?? null,
      sublocalities: address?.sublocalities ?? null,
      formatted: address?.formatted ?? null,
      latitude: address?.latitude ?? null,
      longitude: address?.longitude ?? null,
      accuracy: address?.accuracy ?? null,
      countryCode: address?.countryCode ?? null,
      countryId: address?.countryId ?? null,
      createdAt: address?.createdAt ?? null,
    };
  }

  getFormData(address?: AddressRequest, isDefault?: boolean, stripeCustomerId?: string) {
    const formData = this.cardControl.value;
    const postalCode = `${formData.postalCode ?? ''}`.trim();
    const billingAddress: AddressRequest | undefined = postalCode
      ? this.billingAddressFrom(postalCode, address)
      : address;
    const paymentMethod: PaymentMethodRequest = {
      cardNumber: formData.cardNumber,
      expirationMonth: formData.expirationMonth,
      expirationYear: formData.expirationYear,
      cvc: formData.cvc,
      sourceUserId: this.sourceUserId,
      sourceBusinessId: this.sourceBusinessId,
      stripeCustomerId: stripeCustomerId ?? null,
      billingAddress,
      default: isDefault ?? false,
      cardAgreement: this.cardAgreement(),
    };
    if (this.cardholderFirstName) {
      paymentMethod.cardHolderFirstName = this.cardholderFirstName;
    }
    if (this.cardholderLastName) {
      paymentMethod.cardHolderLastName = this.cardholderLastName;
    }

    return paymentMethod;
  }

  getCardUpdateFormData(
    formData: any,
    stripeCustomerId: string | undefined,
    address?: AddressRequest,
  ): PaymentMethodUpdateTokenProviderRequest {
    const updateRequest = new PaymentMethodUpdateTokenProviderRequest();
    updateRequest.id = this.paymentMethodId;
    updateRequest.token = null; // Not needed when cardNumber is provided
    updateRequest.cardNumber = formData.cardNumber?.replace(/\s/g, '');
    updateRequest.cvc = formData.cvc;
    updateRequest.expirationMonth = parseInt(formData.expirationMonth, 10);
    updateRequest.expirationYear = parseInt(formData.expirationYear, 10);
    updateRequest.sourceUserId = this.sourceUserId;
    updateRequest.sourceBusinessId = this.sourceBusinessId;
    updateRequest.stripeCustomerId = stripeCustomerId ?? null;
    const postalCode = `${formData.postalCode ?? ''}`.trim();
    updateRequest.billingAddress = postalCode
      ? this.billingAddressFrom(postalCode, address)
      : null;
    updateRequest.cardAgreement = this.cardAgreement();
    updateRequest.cardHolderFirstName = this.cardholderFirstName || null;
    updateRequest.cardHolderLastName = this.cardholderLastName || null;

    return updateRequest;
  }

  getCvcFormData(
    cvc: string,
    stripeCustomerId: string | undefined,
    paymentMethod: PaymentMethodData
  ): PaymentMethodUpdateTokenProviderRequest {
    const updateRequest = new PaymentMethodUpdateTokenProviderRequest();
    updateRequest.id = paymentMethod.id;
    updateRequest.token = paymentMethod.token;
    updateRequest.cvc = cvc;
    updateRequest.expirationMonth = paymentMethod.expirationMonth;
    updateRequest.expirationYear = paymentMethod.expirationYear;
    updateRequest.sourceUserId = this.sourceUserId;
    updateRequest.sourceBusinessId = this.sourceBusinessId;
    updateRequest.stripeCustomerId = stripeCustomerId ?? null;

    return updateRequest;
  }

  private addPaymentMethod(userId: string, paymentMethod: PaymentMethodRequest) {
    const token = this.authModel?.token;
    if (!token) {
      this.postAuthFailure(
        'AUTH_MISSING',
        'Authentication token unavailable.'
      );
      return;
    }

    return this.paymentService
      .createPaymentMethod(token, userId, paymentMethod)
      .pipe(
        tap((data: any) => {
          this.post({
            type: 'PAYMENT_RESULT',
            status: 'success',
            payload: data,
          });
        }),
        catchError((err) => {
          this.post({
            type: 'PAYMENT_RESULT',
            status: 'error',
            errorCode: 'SERVER_ERROR',
            errorMessage: err?.message ?? 'Unknown error',
          });
          return EMPTY;
        })
      )
      .subscribe();
  }

  private updatePaymentMethod(
    userId: string,
    updateRequest: PaymentMethodUpdateTokenProviderRequest
  ) {
    const token = this.authModel?.token;
    if (!token) {
      this.postAuthFailure(
        'AUTH_MISSING',
        'Authentication token unavailable.'
      );
      return;
    }

    return this.paymentService
      .updatePaymentMethod(token, userId, updateRequest)
      .pipe(
        tap((data: any) => {
          this.post({
            type: 'PAYMENT_RESULT',
            status: 'success',
            payload: data,
          });
        }),
        catchError((err) => {
          this.post({
            type: 'PAYMENT_RESULT',
            status: 'error',
            errorCode: 'SERVER_ERROR',
            errorMessage: err?.message ?? 'Unknown error',
          });
          return EMPTY;
        })
      )
      .subscribe();
  }

  private handleAuthFailure(message: string) {
    this.authError = message;
    this.cardControl.disable({ emitEvent: false });
    this.postAuthFailure('AUTH_FAILED', message);
  }

  private postAuthFailure(errorCode: string, message: string) {
    this.post({
      type: 'PAYMENT_AUTH_FAILED',
      errorCode,
      errorMessage: message,
    });
  }
}
